import dict from 'config/dictionary';
import externalLinks from 'config/externalLinks';
import { path, pathOr } from 'ramda';
import React from 'react';
import { interpolateMany } from './interpolate';
import { brandedDictionary, hugggOperator } from './recipientBranding';

const userFriendlyStatuses = [422, 404, 500];

export const TILLO_VOUCHER_TYPES = ['tillo', 'wegift'];

export const isTilloVoucher = (voucherType) =>
  TILLO_VOUCHER_TYPES.includes(voucherType);

// Machine-readable code voucher-core sends when a supplier (currently MBL
// Solutions / Greggs) has blocked ordering. Mirrors the backend wire contract;
// keep the string identical to service-framework's ErrorCode value.
export const SUPPLIER_BLOCKED_ORDER_DUE_TO_INVOICING =
  'SUPPLIER_BLOCKED_ORDER_DUE_TO_INVOICING';

export const isSupplierError = (error) =>
  error?.code === SUPPLIER_BLOCKED_ORDER_DUE_TO_INVOICING;

const supportLink = (
  <a href={externalLinks.email.support.url}>
    {externalLinks.email.support.label}
  </a>
);

export const fallbackError = (
  itemType = 'item',
  action = 'redeeming',
  operator = hugggOperator,
) => {
  const lines = brandedDictionary(operator).unknownAPIError;
  return lines.map((text, index) => (
    <React.Fragment key={index}>
      {interpolateMany(text, [
        ['[item_type]', <>{itemType}</>],
        ['[action]', <>{action}</>],
        ['[support_email]', supportLink],
      ])}
      {index < lines.length - 1 && (
        <>
          <br />
          <br />
        </>
      )}
    </React.Fragment>
  ));
};

const renderErrorLines = (lines, replacements) =>
  lines.map((text, index) => (
    <React.Fragment key={index}>
      {interpolateMany(text, replacements)}
      {index < lines.length - 1 && (
        <>
          <br />
          <br />
        </>
      )}
    </React.Fragment>
  ));

export const tilloVoucherError = (brandName, operator = hugggOperator) => {
  const possessive = brandName ? `${brandName}'s` : `this brand's`;
  return renderErrorLines(brandedDictionary(operator).tilloVoucherError, [
    ['[brand_name]', <>{possessive}</>],
    ['[support_email]', supportLink],
  ]);
};

export const voucherRedemptionError = (operator = hugggOperator) =>
  renderErrorLines(brandedDictionary(operator).voucherRedemptionError, [
    ['[support_email]', supportLink],
  ]);

/**
 * The generic error for an operator: Huggg keeps "item"/"redeeming"; an
 * operator with its own product noun (e.g. "link") is "claiming" it.
 */
export const genericError = (operator = hugggOperator) =>
  operator.productNoun
    ? fallbackError(operator.productNoun, 'claiming', operator)
    : fallbackError('item', 'redeeming', operator);

export const supplierVoucherError = () =>
  renderErrorLines(dict.voucherRedemptionErrorSupplier, []);

/**
 * The request never got an HTTP response: DNS, TLS, an offline device, a
 * blocked host or the client-side timeout. The redux module stores the raw
 * axios error in this case (there is no response.data to store), so this is
 * the shape to test for. Operator-neutral copy: nothing to contact, just retry.
 */
export const isNetworkError = (error) =>
  Boolean(error?.isAxiosError && !error.response);

export const networkError = () => renderErrorLines(dict.networkError, []);

/**
 * The copy for a failed voucher claim, shared by the redeem and choose screens
 * so both explain a failure the same way.
 *
 * @param {Object} params
 * @param {Object} params.error the value stored by redux/modules/voucher
 * @param {string} [params.voucherType] the segment's voucherType
 * @param {string} [params.brandName] the huggg's brand name for tillo copy
 * @param {Object} [params.operator]
 * @returns {React.ReactNode|null} null when this voucher type shows its own
 * error UI (prepaid card)
 */
export const voucherClaimErrorContent = ({
  error,
  voucherType,
  brandName,
  operator = hugggOperator,
}) => {
  if (voucherType === 'prepaid-card') {
    return null;
  }

  if (isNetworkError(error)) {
    return networkError();
  }

  // A supplier block (e.g. MBLS/Greggs) is not something CS can fix, so show
  // the "try again later" message instead of pointing the recipient at CS.
  // Keyed on the error code so every other error class is unaffected.
  if (isSupplierError(error)) {
    return supplierVoucherError();
  }

  return isTilloVoucher(voucherType)
    ? tilloVoucherError(brandName, operator)
    : voucherRedemptionError(operator);
};

/**
 * @deprecated This will be removed at some point
 * @param {Object} err
 */
export const translateAPIError = (err, operator = hugggOperator) => {
  // Raw API text may name Huggg support; an operator that hides that gets
  // its own generic copy instead.
  if (!operator.exposeApiErrorMessages) return genericError(operator);
  let status = Number(path(['response', 'status'], err));
  let message = userFriendlyStatuses.includes(status)
    ? pathOr(
        path(['response', 'data', 'message'], err),
        ['response', 'data', 'error'],
        err,
      )
    : null;
  return message || fallbackError();
};
